import { ApiError } from './errors'
import { env } from '@/config/env'

/**
 * Chat transport.
 *
 * Hand-written rather than generated: the send/stream/confirm trio is a small custom
 * protocol (POST to start, SSE to follow) that OpenAPI cannot describe end to end.
 */

export interface StartTurnResponse {
  turn_id: string
  conversation_id: string
}

/** One finished turn, as the server remembers it. */
export interface StoredTurn {
  turn_id: string
  question: string
  answer?: string | null
  status: string
  tool_calls: Array<{
    tool: string
    risk?: string | null
    status: string
    latency_ms?: number | null
    error_type?: string | null
  }>
  total_latency_ms?: number | null
  error_message?: string | null
  created_at: string
}

/** The conversation this browser should be showing. */
export interface CurrentConversation {
  conversation_id: string | null
  count: number
  turns: StoredTurn[]
}

/** One thread in the conversation list. */
export interface ConversationSummary {
  conversation_id: string
  title: string
  turn_count: number
  created_at: string
  updated_at: string
}

/** A thread the user has switched to, with what is in it. */
export interface OpenedConversation {
  conversation_id: string
  title?: string | null
  count: number
  turns: StoredTurn[]
}

/** A turn the server is still holding open for this user. */
export interface ActiveTurn {
  turn_id: string
  conversation_id: string
  question: string
  awaiting_call_ids: string[]
  created_at: string
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) {
    throw new ApiError({
      errorType: 'HTTP_ERROR',
      message: `Request failed (${response.status}).`,
      status: response.status,
    })
  }
  return (await response.json()) as T
}

async function send<T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${env.apiBaseUrl}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? { Accept: 'application/json' } : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError({
      errorType: 'NETWORK_ERROR',
      message: 'Cannot reach the API. Check that the backend is running.',
      status: 0,
      retryable: true,
    })
  }

  if (!response.ok) {
    let message = `Request failed (${response.status}).`
    let errorType = 'HTTP_ERROR'
    try {
      const payload = await response.json()
      if (typeof payload?.message === 'string') message = payload.message
      if (typeof payload?.error_type === 'string') errorType = payload.error_type
    } catch {
      /* keep the generic message */
    }
    throw new ApiError({
      errorType: errorType as ApiError['errorType'],
      message,
      status: response.status,
      requestId: response.headers.get('x-request-id'),
    })
  }

  return (await response.json()) as T
}

/** POST is the common case; it is the same request with the verb filled in. */
const post = <T,>(path: string, body: unknown): Promise<T> => send<T>('POST', path, body)

export const chatApi = {
  /**
   * The caller's own turns that have not finished.
   *
   * How a browser finds its way back to a turn it forgot. Chat state lives in React, so a
   * remount, a navigation or a refresh destroys it — while the server is still holding the
   * turn open at its approval gate. This is the only handle that survives; everything after
   * it reuses the stream, the call id and the approve/reject endpoints that already exist.
   */
  activeTurns: () => get<{ count: number; turns: ActiveTurn[] }>('/api/chat/turns/active'),

  /**
   * Which conversation am I in, and what has been said in it?
   *
   * The question the browser cannot answer for itself once React state is gone. Reading
   * only: opening Chat must never start or change a conversation.
   */
  currentConversation: () => get<CurrentConversation>('/api/chat/conversations/current'),

  /** Begin a new conversation, explicitly. Nothing else does this. */
  startConversation: () => post<{ conversation_id: string }>('/api/chat/conversations', {}),

  /** Every thread this user has, newest activity first. */
  conversations: () =>
    get<{ count: number; conversations: ConversationSummary[] }>('/api/chat/conversations'),

  /**
   * Switch to an existing thread.
   *
   * A POST because it writes: the server records which thread is being read, so returning
   * to Chat later lands here rather than in whichever conversation is newest.
   */
  openConversation: (conversationId: string) =>
    post<OpenedConversation>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/open`,
      {},
    ),

  renameConversation: (conversationId: string, title: string) =>
    send<ConversationSummary>(
      'PATCH',
      `/api/chat/conversations/${encodeURIComponent(conversationId)}`,
      { title },
    ),

  deleteConversation: (conversationId: string) =>
    send<{ deleted: boolean }>(
      'DELETE',
      `/api/chat/conversations/${encodeURIComponent(conversationId)}`,
    ),

  send: (conversationId: string | null, content: string) =>
    post<StartTurnResponse>('/api/chat/messages', {
      conversation_id: conversationId,
      content,
    }),

  streamUrl: (turnId: string) => `${env.apiBaseUrl}/api/chat/stream?turn_id=${encodeURIComponent(turnId)}`,


  cancel: (turnId: string) =>
    post<{ ok: boolean }>(`/api/chat/turns/${encodeURIComponent(turnId)}/cancel`, {}),
}
